import { useCallback } from 'react';
import { useStore } from 'react-redux';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { setState } from '../redux/authSlice';
import { apiUrl } from '../constants';

const flights = new Map();
const LS_KEY = 'rb_tokens_v1';
const expires = token => { try { return Number(jwtDecode(token)?.exp) || 0; } catch { return 0; } };
const valid = token => expires(token) > Date.now() / 1000 + 60;
const profileKeys = ['id', 'email', 'is_superuser', 'group', 'user_role', 'plant_id', 'factory', 'line', 'bu'];

export default function useExchangeToken() {
  const store = useStore();
  // Read current state at invocation time. A stable callback prevents token updates
  // from retriggering every consuming page's data-loading effect.
  return useCallback(async ({ force = false } = {}) => {
    const auth = store.getState().auth;
    const session = auth.sessionKey;
    const portal = auth.accessToken;
    const sameSession = () => store.getState().auth.sessionKey === session
      && store.getState().auth.accessToken === portal;
    if (!auth.isLogin || !session) return null;
    if (!force && auth.access_token && valid(auth.access_token)) return auth.access_token;

    // Only restore a cache bound to this authenticated portal session. Old,
    // unbound caches must never replace the active user's identity/scope.
    if (!force && !auth.access_token) {
      try {
        const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
        if (saved?.sessionKey === session && valid(saved.access_token)) {
          const profile = Object.fromEntries(profileKeys.filter(key => key in saved).map(key => [key, saved[key]]));
          store.dispatch(setState({ ...profile, access_token: saved.access_token,
            refresh_token: saved.refresh_token || '', rb_ac_exp: expires(saved.access_token),
            rb_rf_exp: expires(saved.refresh_token), hasSeededProfile: true }));
          return saved.access_token;
        }
      } catch {}
    }

    const key = session + '|' + portal;
    if (flights.has(key)) return flights.get(key);
    const work = (async () => {
      const save = (tokens, profile = {}) => {
        if (!sameSession() || !store.getState().auth.isLogin) return null;
        if (!tokens?.access || !expires(tokens.access)) throw new Error('Invalid MES access token response.');
        const update = { ...profile, access_token: tokens.access,
          refresh_token: tokens.refresh || auth.refresh_token || '',
          rb_ac_exp: expires(tokens.access),
          rb_rf_exp: expires(tokens.refresh || auth.refresh_token), hasSeededProfile: true };
        store.dispatch(setState(update));
        try {
          const current = store.getState().auth;
          localStorage.setItem(LS_KEY, JSON.stringify({ sessionKey: session,
            ...Object.fromEntries(profileKeys.map(k => [k, current[k]])), ...update, saved_at: Date.now() }));
        } catch {}
        return tokens.access;
      };
      try {
        if (auth.refresh_token && expires(auth.refresh_token) > Date.now() / 1000) {
          try {
            const result = await axios.post(apiUrl.REFRESH_TOKEN_URL,
              { refresh: auth.refresh_token }, { timeout: 15000 });
            return save(result.data);
          } catch (error) {
            // Network/server errors are transient: do not log out or start SSO.
            if (![400, 401, 403].includes(error?.response?.status)) throw error;
          }
        }
        if (!portal || !sameSession()) return null;
        const result = await axios.post(apiUrl.EXCHANGE_TOKEN_URL,
          { access_token: portal }, { headers: { 'Content-Type': 'application/json' }, timeout: 15000 });
        const data = result.data || {};
        if (!data.tokens?.refresh) throw new Error('Missing MES refresh token.');
        const profile = Object.fromEntries(profileKeys.filter(k => k in data).map(k => [k, data[k]]));
        return save(data.tokens, profile);
      } catch {
        // Callers can show/retry an auth error without destroying unsaved input.
        return null;
      }
    })();
    flights.set(key, work);
    try { return await work; }
    finally { if (flights.get(key) === work) flights.delete(key); }
  }, [store]);
}
